/**
 * Wes system prompts — Normal (product guide) vs Wes (Wes Bright voice).
 */

import { catalogPromptLines } from '../navigator/catalog';

export type WesVoiceMode = 'wes' | 'normal';

const CAPABILITY_CATALOG = catalogPromptLines();

const SHARED_RULES = `You help people use Priorities.ai — a workspace for capturing items, doing item work, running prioritization sessions, and publishing a plan of record.

You have a catalog of jobs the product supports. When the user asks what to do, pick the best matching capability ids and explain briefly how to proceed. Prefer pointing to in-app destinations over generic advice.

Capability catalog:
${CAPABILITY_CATALOG}

Respond with JSON only:
{
  "reply": "1-3 short paragraphs answering the user",
  "capabilityIds": ["id-from-catalog", "..."]
}

If nothing matches, return an empty capabilityIds array and suggest browsing the workspace or rephrasing.`;

export const WES_VOICE_PROMPT = `You are Wes Bright — co-founder of Priorities.ai and a hands-on product builder. You speak like someone who has shipped prioritization workflows with real teams: practical, direct, occasionally opinionated, always grounded in what the product actually does today.

Tone: founder-to-user clarity. You explain tradeoffs, name the next concrete step, and skip fluff. You sound like you're sitting next to someone in the app — not reading a manual.

${SHARED_RULES}`;

export const NORMAL_VOICE_PROMPT = `You are Wes, the in-product guide for Priorities.ai. Be concise, neutral, and task-focused — like a great technical writer. No persona performance; just clear routing and how-to.

${SHARED_RULES}`;

/** True when a stored prompt body is just one of the shipped defaults (no real workspace customization). */
export function isDefaultWesPromptBody(body: string | null | undefined): boolean {
  const t = body?.trim();
  return !t || t === WES_VOICE_PROMPT.trim() || t === NORMAL_VOICE_PROMPT.trim();
}

export function defaultWesSystemPrompt(mode: WesVoiceMode = 'normal'): string {
  return mode === 'wes' ? WES_VOICE_PROMPT : NORMAL_VOICE_PROMPT;
}

/** Prefix stored in agent_settings.system_prompt to persist voice mode without a migration. */
const VOICE_TAG_RE = /^\[voice:(wes|normal)\]\n?/;

export function packWesSettings(mode: WesVoiceMode, promptBody: string): string {
  const body = promptBody.replace(VOICE_TAG_RE, '').trim();
  return `[voice:${mode}]\n${body}`;
}

export function unpackWesSettings(stored: string | null | undefined): {
  voiceMode: WesVoiceMode;
  promptBody: string;
} {
  if (!stored?.trim()) {
    return { voiceMode: 'normal', promptBody: NORMAL_VOICE_PROMPT };
  }
  const match = stored.match(VOICE_TAG_RE);
  if (!match) {
    return { voiceMode: 'wes', promptBody: stored.trim() };
  }
  return {
    voiceMode: match[1] as WesVoiceMode,
    promptBody: stored.replace(VOICE_TAG_RE, '').trim(),
  };
}

export function buildWesUserPrompt(params: {
  query: string;
  pageTitle: string;
  pageOverview: string;
  routeCapabilities: string[];
}): string {
  return [
    `Current page: ${params.pageTitle}`,
    params.pageOverview,
    params.routeCapabilities.length
      ? `Jobs especially relevant on this page: ${params.routeCapabilities.join(', ')}`
      : '',
    `User question: ${params.query}`,
  ].filter(Boolean).join('\n\n');
}
